import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from './Layout';
import { getAllConstellations } from './utils/constellationData';
import { Card, GalleryZoomImage } from './components';

// 四方神兽分组数据
const DIVINE_BEASTS = [
  { name: '青龙', emoji: '🐉', color: '#3B82F6', filter: (c: { id: string }) => ['jiao','kang','di','fang','xin','wei','ji'].includes(c.id) },
  { name: '玄武', emoji: '🐢', color: '#10B981', filter: (c: { id: string }) => ['dou','niu','nv','xu','wei2','shi','bi'].includes(c.id) },
  { name: '白虎', emoji: '🐯', color: '#EF4444', filter: (c: { id: string }) => ['kui','lou','wei3','mao','bi2','zi','shen'].includes(c.id) },
  { name: '朱雀', emoji: '🦅', color: '#8B5CF6', filter: (c: { id: string }) => ['jing','gui','liu','xing','zhang','yi','zhen'].includes(c.id) },
];

const CulturePage: React.FC = () => {
  const navigate = useNavigate();
  const allConstellations = getAllConstellations();
  
  // 筛选状态
  const [activeBeast, setActiveBeast] = useState<string>('all');
  const [activeConstellation, setActiveConstellation] = useState<typeof allConstellations[0] | null>(null);

  // 图片灯箱状态 — 记录当前索引，范围由 filteredConstellations 决定
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const closeLightbox = () => setLightboxIndex(null);

  // 页面加载时滚动到页首
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 根据筛选获取星宿
  const filteredConstellations = activeBeast === 'all'
    ? allConstellations
    : allConstellations.filter(c => DIVINE_BEASTS.find(b => b.name === activeBeast)?.filter(c));

  // 灯箱图片列表（必须在 filteredConstellations 之后声明）
  const lightboxImages = filteredConstellations.map(c => ({
    src: c.sculptureImageUrl || c.sculptureThumbnailUrl || '',
    name: c.name,
  }));

  const lightboxImg = lightboxIndex !== null ? lightboxImages[lightboxIndex] : null;

  const goToPrev = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex(i => i !== null ? (i - 1 + lightboxImages.length) % lightboxImages.length : null);
  };

  const goToNext = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex(i => i !== null ? (i + 1) % lightboxImages.length : null);
  };

  // ESC 关闭灯箱 + 方向键切换
  React.useEffect(() => {
    if (lightboxIndex === null) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [lightboxIndex]);

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <Layout>
      <motion.div 
        className="min-h-screen bg-gradient-to-b from-brand-primary to-brand-secondary p-4 md:p-8 relative overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      >
        {/* 星光背景 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="star-field"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto pt-4 md:pt-6">
          
          {/* ===== 标题区 ===== */}
          <motion.div className="text-center mb-8" variants={itemVariants}>
            <h1 className="text-3xl md:text-4xl font-bold text-brand-accent mb-3 font-serif-chinese tracking-wider">
              二十八星宿彩塑
            </h1>
            <p className="text-neutral-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              山西晋城玉皇庙始建于北宋，庙内二十八星宿彩塑为元代遗存，<br/>
              每尊神像神态各异，造型生动，是研究中国古代雕塑艺术的珍贵实物资料。
            </p>
          </motion.div>

          {/* ===== 四方神兽介绍 ===== */}
          <motion.section className="mb-12" variants={itemVariants}>
            <h2 className="text-xl md:text-2xl font-bold text-brand-accent mb-6 text-center font-serif-chinese">
              四方神兽
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {DIVINE_BEASTS.map((beast) => (
                <motion.div
                  key={beast.name}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 border border-neutral-800 text-center cursor-pointer transition-all hover:border-brand-accent/50"
                  onClick={() => setActiveBeast(activeBeast === beast.name ? 'all' : beast.name)}
                  role="button"
                  aria-label={`筛选${beast.name}星宿`}
                >
                  <div 
                    className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-2xl"
                    style={{ backgroundColor: `${beast.color}20`, border: `1px solid ${beast.color}40` }}
                  >
                    {beast.emoji}
                  </div>
                  <h3 className="text-base font-semibold text-brand-accent font-serif-chinese">{beast.name}</h3>
                  <p className="text-neutral-500 text-xs mt-1">
                    {allConstellations.filter(c => beast.filter(c)).length} 宿
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ===== 筛选标签 ===== */}
          <motion.div className="flex justify-center gap-2 md:gap-3 mb-8 flex-wrap" variants={itemVariants}>
            <button
              onClick={() => setActiveBeast('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                activeBeast === 'all'
                  ? 'bg-brand-accent text-brand-primary border-brand-accent shadow-gold-sm'
                  : 'bg-transparent text-neutral-400 border-neutral-700 hover:border-brand-accent/50 hover:text-brand-accent'
              }`}
            >
              全部 ({allConstellations.length})
            </button>
            {DIVINE_BEASTS.map((beast) => (
              <button
                key={beast.name}
                onClick={() => setActiveBeast(activeBeast === beast.name ? 'all' : beast.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                  activeBeast === beast.name
                    ? 'bg-brand-accent text-brand-primary border-brand-accent shadow-gold-sm'
                    : 'bg-transparent text-neutral-400 border-neutral-700 hover:border-brand-accent/50 hover:text-brand-accent'
                }`}
              >
                {beast.emoji} {beast.name}
              </button>
            ))}
          </motion.div>

          {/* ===== 塑像画廊 ===== */}
          <motion.section variants={itemVariants}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-brand-accent font-serif-chinese">
                玉皇庙彩塑图集
              </h2>
              <p className="text-neutral-500 text-sm">
                共 {filteredConstellations.length} 尊
              </p>
            </div>
            
            <Card variant="default" shadow="lg" className="p-4 md:p-6">
              <div className="constellation-gallery" role="list" aria-label="玉皇庙二十八星宿彩塑">
                {filteredConstellations.map((c) => (
                  <div
                    key={c.id}
                    className="constellation-gallery-item"
                    data-name={c.name}
                    role="listitem"
                    aria-label={`${c.name} - ${c.description}，点击查看大图`}
                    tabIndex={0}
                    onClick={() => setLightboxIndex(filteredConstellations.findIndex(fc => fc.id === c.id))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setLightboxIndex(filteredConstellations.findIndex(fc => fc.id === c.id));
                      }
                    }}
                  >
                    <img
                      src={c.sculptureThumbnailUrl || c.sculptureImageUrl || ''}
                      alt={`${c.name}塑像`}
                      loading="lazy"
                      draggable={false}
                    />
                    {/* 悬浮显示名称和放大图标 */}
                    <div className="constellation-gallery-item-overlay">
                      <span className="constellation-gallery-item-name">{c.name}</span>
                      <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.section>

          {/* ===== 文化介绍 ===== */}
          <motion.section className="mt-16 mb-12" variants={itemVariants}>
            <h2 className="text-xl md:text-2xl font-bold text-brand-accent mb-6 text-center font-serif-chinese">
              关于玉皇庙
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card variant="glass" shadow="md" className="p-6">
                <h3 className="text-lg font-bold text-brand-accent mb-3 font-serif-chinese">🏛️ 历史沿革</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  玉皇庙位于山西省晋城市区东关，创建于北宋神宗元丰年间（1078-1085），<br/>
                  距今已有近千年历史。庙内建筑保存完整，尤其是二十八星宿殿内的彩塑，
                  为元代雕塑家刘銮及其弟子所塑，代表了元代彩塑艺术的最高成就。
                </p>
              </Card>
              <Card variant="glass" shadow="md" className="p-6">
                <h3 className="text-lg font-bold text-brand-accent mb-3 font-serif-chinese">🎨 艺术价值</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  二十八尊星宿像高约1.8米，形态各异，神态生动，或庄重威严，或和蔼可亲，
                  或怒目圆睁，或沉思冥想，充分展现了古代雕塑家对人体结构的深刻理解
                  和对人物性格的精准把握。
                </p>
              </Card>
              <Card variant="glass" shadow="md" className="p-6">
                <h3 className="text-lg font-bold text-brand-accent mb-3 font-serif-chinese">⭐ 二十八宿</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  中国古代天文学家将星空划分为二十八个区域，称为"二十八宿"，
                  并将每宿与一种动物联系起来，形成东方青龙、西方白虎、南方朱雀、北方玄武
                  四象，每象七宿，共二十八宿。
                </p>
              </Card>
              <Card variant="glass" shadow="md" className="p-6">
                <h3 className="text-lg font-bold text-brand-accent mb-3 font-serif-chinese">🧧 文化传承</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  二十八星宿是中国传统文化的重要组成部分，在道教文化中占有重要地位。
                  每尊神像都有其独特的象征意义和守护职能，反映了古人将天文现象
                  人格化、神格化的独特思维方式。
                </p>
              </Card>
            </div>
          </motion.section>


        </div>
      </motion.div>

      {/* ===== 星宿详情弹窗 ===== */}
      {activeConstellation && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setActiveConstellation(null)}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
          <motion.div 
            className="relative bg-brand-secondary border border-brand-accent/30 rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <button
              className="absolute top-4 right-4 text-neutral-400 hover:text-white transition-colors"
              onClick={() => setActiveConstellation(null)}
              aria-label="关闭"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* 塑像图片 */}
            <div 
              className="cursor-pointer mb-6"
              onClick={() => setLightboxIndex(filteredConstellations.findIndex(fc => fc.id === activeConstellation.id))}
            >
              <img
                src={activeConstellation.sculptureThumbnailUrl || activeConstellation.sculptureImageUrl || ''}
                alt={`${activeConstellation.name}塑像`}
                className="w-full h-48 object-contain rounded-xl border border-brand-accent/20"
              />
              <p className="text-neutral-500 text-xs mt-2 text-center">点击查看大图</p>
            </div>

            {/* 星宿信息 */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-accent/10 border border-brand-accent/20 text-brand-accent text-sm mb-3">
                <span>{activeConstellation.element}</span>
                <span className="text-brand-accent/40">|</span>
                <span>{activeConstellation.animal}</span>
              </div>
              <h3 className="text-3xl font-bold text-brand-accent mb-2 font-comic-chinese">
                {activeConstellation.name}
              </h3>
              <p className="text-neutral-400 text-sm">
                {activeConstellation.description}
              </p>
            </div>

            {/* 塑像特征 */}
            <div className="bg-neutral-900/50 rounded-xl p-4 mb-4">
              <h4 className="text-sm font-semibold text-brand-accent mb-2">🗿 塑像特征</h4>
              <p className="text-neutral-300 text-sm leading-relaxed">
                {activeConstellation.origin_statue_feature}
              </p>
            </div>

            {/* 性格关键词 */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-brand-accent mb-3">✨ 性格特质</h4>
              <div className="flex flex-wrap gap-2">
                {activeConstellation.personalityKeywords.map((keyword, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1 bg-neutral-800 rounded-full text-neutral-300 text-xs"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* 测算按钮 */}
            <button
              onClick={() => navigate(`/result/${activeConstellation.id}`)}
              className="w-full py-3 bg-brand-accent text-brand-primary font-bold rounded-xl hover:bg-brand-accent/90 transition-colors"
            >
              测算本命星宿
            </button>
          </motion.div>
        </div>
      )}

      {/* ===== 图片灯箱：支持手势缩放 + 左右滑动预览 ===== */}
      {lightboxImg && lightboxIndex !== null && (
        <div
          className="lightbox-backdrop"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${lightboxImg.name}大图预览`}
        >
          <button
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="关闭大图预览"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* 左箭头 */}
          <button
            className="lightbox-nav lightbox-nav-prev"
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            aria-label="上一张"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* 右箭头 */}
          <button
            className="lightbox-nav lightbox-nav-next"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            aria-label="下一张"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="lightbox-title">
            {lightboxImg.name}
            <span className="lightbox-counter">{lightboxIndex + 1} / {lightboxImages.length}</span>
          </div>
          <div className="lightbox-zoom-container" onClick={(e) => e.stopPropagation()}>
            <GalleryZoomImage
              index={lightboxIndex}
              items={lightboxImages.map(img => ({ src: img.src, alt: img.name }))}
              onIndexChange={setLightboxIndex}
            />
          </div>
          <div className="lightbox-hint">左右滑动切换 · 双指或滚轮缩放</div>
        </div>
      )}
    </Layout>
  );
};

export default CulturePage;
